import { useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

const BASE = "/claim-mdr";

/**
 * Impor Data Claim — berkas tiap leasing BENTUKNYA BEDA (jawaban Bos, 20 Okt).
 *
 * Alurnya dua langkah dan itu disengaja:
 *   1. PRATINJAU — server membaca sendiri berkasnya (judul → isi) dan menebak
 *      peta kolomnya. `siap_otomatis` memberi tahu apakah tebakannya sudah
 *      cukup kuat (seluruh medan wajib dikenali dari JUDUL, bukan dari isi).
 *   2. IMPOR — peta yang sudah dilihat mata manusia dikirim balik. Kalau
 *      leasingnya dipilih, petanya disimpan jadi PROFIL, dan berkas berikutnya
 *      dari leasing itu tidak ditanya lagi.
 *
 * Yang TIDAK dilakukan layar ini: menebak sendiri. Seluruh tebakan datang dari
 * `deteksi.py`, dan daftar medan yang bisa dipetakan datang dari
 * `/claim-mdr/meta` (`medan_impor`) — bukan diketik di sini, supaya kolom baru
 * yang sudah bisa dibaca backend langsung muncul sebagai pilihan.
 */
export default function ClaimMdrImporModal({ leasing = [], medanImpor = [], onTutup, onSelesai }) {
  const inputRef = useRef(null);
  const [berkas, setBerkas] = useState(null);
  const [kodeLeasing, setKodeLeasing] = useState("");
  const [pratinjau, setPratinjau] = useState(null);
  const [peta, setPeta] = useState({});        // {medan: indeks kolom}
  const [sibuk, setSibuk] = useState("");
  const [hasil, setHasil] = useState(null);

  const judul = pratinjau?.judul_berkas || [];
  const wajib = useMemo(() => medanImpor.filter((m) => m.wajib).map((m) => m.kode), [medanImpor]);

  const kurangWajib = wajib.filter((m) => peta[m] === undefined || peta[m] === null);

  // Dua medan menunjuk SATU kolom hampir selalu salah tempel — dan kalau
  // kolomnya kolom uang, akibatnya tidak terlihat: memetakan Nominal dan SRP
  // ke kolom yang sama membuat DP = 0 tanpa satu galat pun (butir 16).
  const kolomGanda = useMemo(() => {
    const per = {};
    Object.entries(peta).forEach(([m, k]) => {
      if (k === undefined || k === null) return;
      (per[k] = per[k] || []).push(m);
    });
    return Object.entries(per).filter(([, ms]) => ms.length > 1);
  }, [peta]);

  const labelMedan = (kode) => medanImpor.find((m) => m.kode === kode)?.label || kode;

  const pilihBerkas = (f) => {
    setBerkas(f || null);
    setPratinjau(null); setPeta({}); setHasil(null);
  };

  const jalankanPratinjau = async () => {
    if (!berkas) return;
    setSibuk("pratinjau"); setHasil(null);
    try {
      const fd = new FormData();
      fd.append("berkas", berkas);
      const r = await api.post(`${BASE}/data-claim/pratinjau`, fd, {
        params: kodeLeasing ? { leasing: kodeLeasing } : {},
      });
      setPratinjau(r.data);
      // Prefill dari tebakan server. Nilai `peta` server berbentuk
      // {medan: {kolom, judul, dari}} — layar menyimpan indeksnya saja, dan
      // mengirimkannya kembali sebagai indeks (bentuk yang dipakai
      // `simpan_profil`, yang membungkusnya sendiri).
      const awal = {};
      Object.entries(r.data.peta || {}).forEach(([m, p]) => {
        const k = typeof p === "object" ? p.kolom : p;
        if (Number.isInteger(k)) awal[m] = k;
      });
      setPeta(awal);
      if (r.data.pesan) toast.warning(r.data.pesan);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Berkasnya tidak bisa dibaca");
    } finally { setSibuk(""); }
  };

  const jalankanImpor = async () => {
    if (!berkas || !pratinjau || kurangWajib.length || kolomGanda.length) return;
    setSibuk("impor");
    try {
      const fd = new FormData();
      fd.append("berkas", berkas);
      const params = { peta: JSON.stringify(peta), baris_judul: pratinjau.baris_judul };
      if (kodeLeasing) params.leasing = kodeLeasing;
      const r = await api.post(`${BASE}/data-claim/impor`, fd, { params });
      setHasil(r.data);
      const masuk = r.data.masuk || 0;
      if (masuk) toast.success(`${masuk} baris Data Claim masuk`);
      else toast.warning("Tidak ada baris baru — semuanya sudah pernah diimpor");
      onSelesai?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengimpor");
    } finally { setSibuk(""); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 overflow-y-auto"
      data-testid="mdr-impor-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Upload className="w-4 h-4 text-slate-400" /> Impor Data Claim
          </h2>
          <button onClick={onTutup} data-testid="mdr-impor-tutup"
            className="p-1 rounded hover:bg-slate-100 text-slate-500" aria-label="Tutup">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* ---- langkah 1: berkas + leasing ---- */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Berkas Excel</label>
              <input ref={inputRef} type="file" accept=".xlsx,.xlsm,.xls"
                onChange={(e) => pilihBerkas(e.target.files?.[0])}
                data-testid="mdr-impor-berkas"
                className="block w-full text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:text-xs file:font-medium" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Leasing <span className="text-slate-400">(agar bentuk berkasnya diingat)</span>
              </label>
              <select value={kodeLeasing} onChange={(e) => setKodeLeasing(e.target.value)}
                data-testid="mdr-impor-leasing"
                className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm">
                <option value="">— tidak disimpan sebagai profil —</option>
                {leasing.map((l) => <option key={l.kode} value={l.kode}>{l.nama}</option>)}
              </select>
            </div>
          </div>

          <button onClick={jalankanPratinjau} disabled={!berkas || !!sibuk}
            data-testid="mdr-impor-pratinjau"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {sibuk === "pratinjau" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Baca berkasnya
          </button>

          {/* ---- langkah 2: peta kolom ---- */}
          {pratinjau && pratinjau.baris_judul >= 0 && (
            <div className="space-y-3 border-t border-slate-200 pt-4" data-testid="mdr-impor-peta">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${
                  pratinjau.siap_otomatis
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  {pratinjau.siap_otomatis
                    ? <><CheckCircle2 className="w-3 h-3" /> Bentuknya dikenali</>
                    : <><AlertTriangle className="w-3 h-3" /> Perlu dicek sekali</>}
                </span>
                <span className="text-slate-500">
                  Judul kolom di baris {pratinjau.baris_judul + 1} · {pratinjau.jumlah_baris} baris data
                </span>
              </div>

              {pratinjau.perlu_konfirmasi?.length > 0 && (
                <p className="text-xs text-amber-700">
                  Ditebak dari ISI kolom (judulnya tidak dikenali):{" "}
                  {pratinjau.perlu_konfirmasi.map(labelMedan).join(", ")} — mohon dicek.
                </p>
              )}

              <div className="grid gap-2 sm:grid-cols-2">
                {medanImpor.map((m) => (
                  <div key={m.kode} className="flex items-center gap-2">
                    <label className="text-xs text-slate-600 w-32 shrink-0">
                      {m.label}{m.wajib && <span className="text-rose-500"> *</span>}
                    </label>
                    <select
                      value={peta[m.kode] === undefined || peta[m.kode] === null ? "" : String(peta[m.kode])}
                      onChange={(e) => setPeta((p) => {
                        const n = { ...p };
                        if (e.target.value === "") delete n[m.kode];
                        else n[m.kode] = Number(e.target.value);
                        return n;
                      })}
                      data-testid={`mdr-impor-medan-${m.kode}`}
                      className="flex-1 min-w-0 px-2 py-1 rounded-lg border border-slate-200 text-xs">
                      <option value="">— tidak ada —</option>
                      {judul.map((j, i) => (
                        <option key={i} value={i}>
                          {i + 1}. {String(j ?? "").trim() || "(tanpa judul)"}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {kolomGanda.length > 0 && (
                <p className="text-xs text-rose-700 flex items-start gap-1.5" data-testid="mdr-impor-kolom-ganda">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  Satu kolom dipakai dua medan:{" "}
                  {kolomGanda.map(([k, ms]) => `kolom ${Number(k) + 1} → ${ms.map(labelMedan).join(" & ")}`).join("; ")}.
                  Betulkan dulu — kalau salah satunya kolom uang, selisihnya tidak akan terlihat di mana pun.
                </p>
              )}

              {kurangWajib.length > 0 && (
                <p className="text-xs text-rose-700" data-testid="mdr-impor-kurang">
                  Kolom wajib belum dipetakan: {kurangWajib.map(labelMedan).join(", ")}
                </p>
              )}

              {/* Contoh isi — supaya salah kolom terlihat SEBELUM diimpor. */}
              {pratinjau.contoh?.length > 0 && (
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="text-[11px]">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        {judul.map((j, i) => (
                          <th key={i} className="px-2 py-1 text-left font-medium whitespace-nowrap">
                            {i + 1}. {String(j ?? "").trim() || "—"}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {pratinjau.contoh.map((r, ri) => (
                        <tr key={ri}>
                          {judul.map((_, i) => (
                            <td key={i} className="px-2 py-1 whitespace-nowrap text-slate-700">
                              {r[i] === null || r[i] === undefined ? "" : String(r[i])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ---- hasil ---- */}
          {hasil && (
            <div className="border-t border-slate-200 pt-4 space-y-2" data-testid="mdr-impor-hasil">
              <p className="text-sm text-slate-800">
                <b>{hasil.masuk || 0}</b> baris baru masuk
                {hasil.duplikat ? <> · {hasil.duplikat} sudah pernah diimpor</> : null}
                {hasil.jumlah_galat ? <> · <span className="text-rose-700">{hasil.jumlah_galat} baris ditolak</span></> : null}
              </p>
              {hasil.galat?.length > 0 && (
                <ul className="max-h-40 overflow-y-auto space-y-0.5">
                  {hasil.galat.map((g, i) => (
                    <li key={i} className="text-[11px] text-rose-700">
                      Baris {g.baris}: {g.pesan}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button onClick={onTutup} data-testid="mdr-impor-batal"
            className="px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">
            Tutup
          </button>
          <button onClick={jalankanImpor} data-testid="mdr-impor-kirim"
            disabled={!pratinjau || !!sibuk || kurangWajib.length > 0 || kolomGanda.length > 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
            {sibuk === "impor" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Impor
          </button>
        </div>
      </div>
    </div>
  );
}
